import React, { useEffect, useState } from "react";
import api, { inr } from "@/lib/api";
import { Link } from "react-router-dom";
import { MapPin, Truck, Phone, Map, Route } from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";

export default function DeliveryRoute() {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState({});
  useEffect(() => {
    api.get("/delivery-route").then(r => setItems(r.data));
    api.get("/settings").then(r => setSettings(r.data)).catch(()=>{});
  }, []);

  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  // Build a multi-stop Google Maps URL — origin = company address, waypoints = each stop, destination = last stop
  const routeMapUrl = (() => {
    const stops = items.map(o => o.address).filter(Boolean);
    if (stops.length === 0) return "";
    const origin = settings.address || settings.map_link || "";
    const destination = stops[stops.length - 1];
    const waypoints = stops.slice(0, -1).join("|");
    const base = "https://www.google.com/maps/dir/?api=1";
    const parts = [
      origin && `origin=${encodeURIComponent(origin)}`,
      `destination=${encodeURIComponent(destination)}`,
      waypoints && `waypoints=${encodeURIComponent(waypoints)}`,
      `travelmode=driving`,
    ].filter(Boolean).join("&");
    return `${base}&${parts}`;
  })();

  const singleStopUrl = (o) => {
    const dest = o.address ? encodeURIComponent(o.address) : "";
    if (!dest) return "";
    const origin = settings.address ? `&origin=${encodeURIComponent(settings.address)}` : "";
    return `https://www.google.com/maps/dir/?api=1&destination=${dest}${origin}&travelmode=driving`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-tiny mb-1 flex items-center gap-1"><MapPin size={11}/> Dispatch</div>
          <h1 className="font-display text-3xl tracking-tight">Today's Route Sheet</h1>
          <p className="text-sm text-[#75706A] mt-1">{today} · {items.length} order{items.length===1?"":"s"} to move</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {routeMapUrl && (
            <a
              href={routeMapUrl} target="_blank" rel="noreferrer"
              className="btn-primary text-sm flex items-center gap-2"
              data-testid="route-optimize-btn"
              title="Opens Google Maps with all stops pre-filled — free"
            >
              <Route size={14}/> Open full route in Google Maps
            </a>
          )}
          <button onClick={()=>window.print()} className="btn-ghost text-sm">Print sheet</button>
        </div>
      </div>

      {!settings.address && items.length > 0 && (
        <div className="card-surface p-3 text-xs text-[#75706A] border-l-4 border-[#D99036]">
          Tip: Set your office address in <Link to="/settings" className="underline">Settings</Link> so the map route can start from your location.
        </div>
      )}

      {items.length === 0 ? (
        <div className="card-surface p-10 text-center text-[#75706A]">
          <Truck size={40} className="mx-auto mb-3 opacity-40"/>
          <div className="font-display font-semibold">Nothing on the run today.</div>
          <div className="text-sm">Orders show up here once they reach packing / QC 2 / dispatched.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((o, i) => {
            const msg = `Hi ${o.customer_name}, your order ${o.order_no} is on its way today. Please keep ₹${(o.balance||0).toLocaleString("en-IN")} ready as balance.`;
            const stopUrl = singleStopUrl(o);
            return (
              <div key={o.id} className="card-surface p-5 fade-up" data-testid={`route-card-${o.id}`}>
                <div className="flex items-start justify-between mb-2 gap-2">
                  <div className="min-w-0">
                    <div className="text-xs text-[#75706A]">Stop {i+1}</div>
                    <Link to={`/orders/${o.id}`} className="font-display font-bold text-lg mono hover:underline">{o.order_no}</Link>
                  </div>
                  <span className="badge badge-warn shrink-0">{o.stage}</span>
                </div>
                <div className="text-sm font-medium truncate">{o.customer_name}</div>
                {o.address && <div className="text-xs text-[#75706A] mt-1 flex items-start gap-1"><MapPin size={11} className="mt-0.5 shrink-0"/><span className="break-words">{o.address}</span></div>}
                {o.customer_phone && <div className="text-xs text-[#75706A] mt-1 flex items-center gap-1"><Phone size={11}/> <span className="mono">{o.customer_phone}</span></div>}
                <div className="mt-3 pt-3 border-t border-[#E7E1D3] flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-xs">
                    <span className="text-[#75706A]">Balance:</span> <span className={`mono font-bold ${o.balance>0?"text-[#B83A3A]":"text-[#4A7258]"}`}>{inr(o.balance)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {stopUrl && <a href={stopUrl} target="_blank" rel="noreferrer" className="btn-ghost text-xs inline-flex items-center gap-1" data-testid={`stop-map-${o.id}`}><Map size={11}/>Map</a>}
                    {o.customer_phone && <WhatsAppButton phone={o.customer_phone} message={msg} label="Ping" size="sm"/>}
                  </div>
                </div>
                {(o.courier || o.tracking) && <div className="text-[10px] text-[#75706A] mt-2">{o.courier} · <span className="mono">{o.tracking}</span></div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
